import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { IChangeSet } from '../changes-dialog/changes-dialog.component';
import { AuditService } from '../services/audit.service';
import { AuditObjectIdCountMap, AuditObjectName } from '../services/audit.service.models';
import { LabpartnerService } from '../services/labpartner.service';
import {
  AssayName,
  AuditedChangeResponse,
  ExperimentType,
  User,
} from '../services/labpartner.service.model';
import { EVENT_ARCHIVE_ASSAY, EVENT_UNARCHIVE_ASSAY } from '../services/logging-constants';
import { LoggingService } from '../services/logging.service';
import { UserAccountService } from '../services/user-account.service';
import { DialogService } from '../shared/dialog.service';
import { NotificationService } from '../shared/notification.service';
import { BaseComponent } from '../support/base.component';
import { AssayDialogComponent } from './assay-dialog/assay-dialog.component';

@Component({
  selector: 'app-manage-assays',
  templateUrl: './manage-assays.component.html',
  styleUrls: ['./manage-assays.component.scss'],
  standalone: false,
})
export class ManageAssaysComponent extends BaseComponent implements AfterViewInit, OnInit {
  @ViewChild(MatPaginator)
  paginator!: MatPaginator;
  assays: AssayName[] = [];
  auditAssayCountMap?: AuditObjectIdCountMap;

  dataSource: MatTableDataSource<AssayName> = new MatTableDataSource();

  showArchivedAssays: boolean = false;

  displayedColumns: string[] = [
    'assayId',
    'assayRevision',
    'assayName',
    'assayShortName',
    'analyteName',
    'analyteShortName',
    'chamber',
    'channel',
    'groupId',
    'instrument',
    'actions',
  ];

  currentRoles: string[] = [];
  user: User | undefined = undefined;

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private labpartnerService: LabpartnerService,
    private accountService: UserAccountService,
    private notificationService: NotificationService,
    private dialogService: DialogService,
    private loggingService: LoggingService,
    private auditService: AuditService,
  ) {
    super();
    this.loadAssays();
  }

  // After the component is initialized, subscribe to the currentRoles observable
  ngOnInit(): void {
    this.accountService.SetUserList(false).then(() => {});

    this.subscription.add(
      this.accountService.currentUser$.subscribe(user => {
        this.user = user;
      }),
    );

    this.subscription.add(
      this.accountService.currentRoles$.subscribe(roles => {
        this.currentRoles = roles;
        if (!this.accountService.isAdmin()) {
          setTimeout(() => {
            this.router.navigate(['/']);
          }, 100);
        }
      }),
    );

    this.subscription.add(
      this.auditService.assayAuditCountMap.subscribe(acm => {
        this.auditAssayCountMap = acm.AssayName;
      }),
    );
  }

  // After the view is initialized, set the paginator
  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  // Clean up the subscription
  protected ngOnDestroyInternal(): void {}

  // Load the assays from the service
  loadAssays() {
    this.subscription.add(
      this.labpartnerService.getAssayNames(true).subscribe(assays => {
        assays.forEach(a => (a.analyteTemplates = a.analyteTemplates.filter(an => an.isActive)));
        this.assays = assays;
        this.setAssays();
      }),
    );
    this.loadAuditCounts();
  }

  // Load the audit counts from the service
  loadAuditCounts() {
    this.subscription.add(this.auditService.getAssaysAuditCounts().subscribe(() => {}));
  }

  // Filter the assays based on the showArchivedAssays flag
  setAssays() {
    if (this.showArchivedAssays) {
      this.dataSource.data = this.assays;
    } else {
      this.dataSource.data = this.assays.filter(assay => assay.isActive == true);
    }

    this.dataSource._updateChangeSubscription();
  }

  // Open the dialog to add a new assay
  addAssay() {
    const dialogRef = this.dialog.open(AssayDialogComponent, { data: { user: this.user } });

    dialogRef.afterClosed().subscribe(dialogResult => {
      if (!dialogResult) {
        return;
      }

      this.loadAssays();
    });
  }

  // Archive an assay
  archiveAssay(assay: AssayName) {
    if (!assay.id) {
      return;
    }

    // Create a changeSet for the changes dialog
    const changeSet: IChangeSet[] = [];
    changeSet.push({
      identifier: `Assay ${assay.id}`,
      field: 'IsActive',
      oldValue: `UNARCHIVED`,
      newValue: 'ARCHIVED',
    });

    // Open the changes dialog
    const dialogRef = this.dialogService.openConfirmChangesDialog(
      changeSet,
      ExperimentType.ValidationAndVerification,
      {
        isDelete: false,
      },
    );

    // Subscribe to the dialog result
    dialogRef.afterClosed().subscribe(async data => {
      if (data?.submitClicked && data?.reasonProvided) {
        if (!assay.id) {
          return;
        }

        this.subscription.add(
          this.labpartnerService.archiveAssayName(assay.id, data.batchReason).subscribe(
            () => {
              if (!assay.id) {
                return;
              }

              this.loadAssays();
              this.notificationService.success('Assay archived');

              const props: { [key: string]: string | number } = {
                AssayId: assay.id,
                UserId: this.user?.userId ?? 0,
              };
              this.loggingService.logEvent(EVENT_ARCHIVE_ASSAY, props);
            },
            (err: any) => {
              const expectedErr = err.error as AuditedChangeResponse;
              if (expectedErr.expectedException) {
                this.notificationService.error(expectedErr.message);
              } else {
                throw err;
              }
            },
          ),
        );
      }
    });
  }

  // Unarchive an assay
  unarchiveAssay(assay: AssayName) {
    if (!assay.id) {
      return;
    }

    // Create a changeSet for the changes dialog
    const changeSet: IChangeSet[] = [];
    changeSet.push({
      identifier: `Assay ${assay.id}`,
      field: 'IsActive',
      oldValue: `ARCHIVED`,
      newValue: 'UNARCHIVED',
    });

    // Open the changes dialog
    const dialogRef = this.dialogService.openConfirmChangesDialog(
      changeSet,
      ExperimentType.ValidationAndVerification,
      {
        isDelete: false,
      },
    );

    // Subscribe to the dialog result
    this.subscription.add(
      dialogRef.afterClosed().subscribe(async data => {
        if (data?.submitClicked && data?.reasonProvided) {
          if (!assay.id) {
            return;
          }

          this.subscription.add(
            this.labpartnerService.unarchiveAssayName(assay.id, data.batchReason).subscribe(
              () => {
                if (!assay.id) {
                  return;
                }

                this.loadAssays();
                this.notificationService.success('Assay unarchived');

                const props: { [key: string]: string | number } = {
                  AssayId: assay.id,
                  UserId: this.user?.userId ?? 0,
                };
                this.loggingService.logEvent(EVENT_UNARCHIVE_ASSAY, props);
              },
              (err: any) => {
                const expectedErr = err.error as AuditedChangeResponse;
                if (expectedErr.expectedException) {
                  this.notificationService.error(expectedErr.message);
                } else {
                  throw err;
                }
              },
            ),
          );
        }
      }),
    );
  }

  showColumnAuditDialog(row: AssayName) {
    if (!row.id) {
      return;
    }

    this.subscription.add(
      this.dialogService.openAssayNameChangelogDialog(
        `Change log for Assay`,
        row.id,
        AuditObjectName.AssayName,
        0,
        undefined,
        false,
      ),
    );
  }
}
